import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { useLang } from "@/i18n";
import { NAV } from "@/content/nav";
import { Eyebrow, Reveal } from "./Reveal";

const THEMES = {
  dark: "bg-[#0A1F18] text-[#FBF8F3]",
  light: "bg-[#FBF8F3] text-[#0A1F18]",
  alt: "bg-[#F3EFE6] text-[#0A1F18]",
};

const muted = (theme) => (theme === "dark" ? "text-[#B0C2B9]" : "text-[#5C6B64]");
const card = (theme) =>
  theme === "dark" ? "border-[#1E4437] bg-[#102920]" : "border-[#E4DDD0] bg-white";
const line = (theme) => (theme === "dark" ? "border-[#1E4437]" : "border-[#E4DDD0]");

const PageHero = ({ c, slug }) => {
  const { lang } = useLang();
  return (
    <div className="relative overflow-hidden bg-[#FBF8F3] pb-20 pt-40 sm:pb-28 sm:pt-48">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rotate-45 border border-[#C87D55]/25" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-[480px] w-[480px] rotate-45 border border-[#C87D55]/15" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]"
          data-testid="page-breadcrumb"
        >
          {NAV[lang].breadcrumbHome} <span className="text-[#B36C45]">›</span> {c.eyebrow}
        </motion.p>
        <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#0A1F18] sm:text-5xl lg:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              {c.title}
            </motion.span>
          </span>
        </h1>
        {c.intro && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-7 max-w-2xl text-base leading-relaxed text-[#5C6B64] sm:text-lg"
            data-testid="page-intro"
          >
            {c.intro}
          </motion.p>
        )}
        {c.chips && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-2"
          >
            {c.chips.map((chip) => (
              <span
                key={chip}
                className="border border-[#C87D55]/40 bg-[#C87D55]/5 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B36C45]"
              >
                {chip}
              </span>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const FaqAccordion = ({ items, theme }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className={`border-t ${line(theme)}`}>
      {items.map((item, i) => (
        <div key={i} className={`border-b ${line(theme)}`} data-testid={`page-faq-${i}`}>
          <button
            data-testid={`page-faq-${i}-trigger`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="group flex w-full items-center justify-between gap-6 py-5 text-left"
          >
            <span className="font-display text-lg font-medium tracking-tight transition-colors group-hover:text-[#B36C45]">
              {item.q}
            </span>
            <Plus className={`h-5 w-5 shrink-0 text-[#C87D55] transition-transform duration-300 ${open === i ? "rotate-45" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <p className={`max-w-2xl pb-6 text-base leading-relaxed ${muted(theme)}`}>{item.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Section = ({ s, index }) => {
  const theme = s.theme === "alt" ? "alt" : index % 2 === 0 ? "light" : "alt";
  const m = muted(theme);

  return (
    <section className={`${THEMES[theme]} py-16 sm:py-24`} data-testid={`page-section-${index}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {s.title && (
          <Reveal className="mb-12 max-w-2xl">
            {s.eyebrow && <Eyebrow dark={theme === "dark"}>{s.eyebrow}</Eyebrow>}
            <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl">
              {s.title}
            </h2>
            {s.sub && <p className={`mt-5 text-base leading-relaxed ${m}`}>{s.sub}</p>}
          </Reveal>
        )}

        {s.type === "text" &&
          s.paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`mb-5 max-w-3xl text-base leading-relaxed ${m}`}>{p}</p>
            </Reveal>
          ))}

        {s.type === "stats" && (
          <div className={`grid grid-cols-2 gap-px border lg:grid-cols-4 ${card(theme)}`}>
            {s.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.08} className={`${theme === "dark" ? "bg-[#102920]" : "bg-white"} p-8`}>
                <span className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{item.value}</span>
                <span className={`mt-2 block font-mono text-[10px] uppercase tracking-[0.2em] ${m}`}>{item.label}</span>
              </Reveal>
            ))}
          </div>
        )}

        {s.type === "grid" && (
          <div className={`grid gap-px border sm:grid-cols-2 ${s.cols === 3 ? "lg:grid-cols-3" : ""} ${card(theme)}`}>
            {s.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.06} className={`group relative h-full ${theme === "dark" ? "bg-[#102920]" : "bg-white"} p-8`}>
                <span className="font-mono text-xs tracking-[0.3em] text-[#C87D55]">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{item.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${m}`}>{item.desc}</p>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-[#C87D55] transition-[width] duration-500 group-hover:w-full" />
              </Reveal>
            ))}
          </div>
        )}

        {s.type === "numbered" && (
          <div>
            {s.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className={`grid items-start gap-3 border-t py-9 sm:grid-cols-[100px_1fr_1.3fr] sm:gap-10 ${line(theme)}`}>
                  <span className="font-mono text-sm tracking-[0.3em] text-[#C87D55]">/ 0{i + 1}</span>
                  <h3 className="font-serif text-2xl font-medium italic tracking-tight">{item.title}</h3>
                  <p className={`text-base leading-relaxed ${m}`}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {s.type === "list" && (
          <Reveal>
            <div className="flex flex-wrap gap-2">
              {s.items.map((item) => (
                <span
                  key={item}
                  className={`border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] ${
                    theme === "dark" ? "border-[#1E4437] text-[#B0C2B9]" : "border-[#E4DDD0] bg-white text-[#5C6B64]"
                  }`}
                >
                  {item}
                </span>
              ))}
            </div>
          </Reveal>
        )}

        {s.type === "quote" && (
          <Reveal>
            <blockquote className={`mx-auto max-w-3xl border-l-2 border-[#C87D55] pl-6 font-serif text-2xl italic leading-relaxed sm:text-3xl ${theme === "dark" ? "text-[#FBF8F3]/90" : "text-[#0A1F18]"}`}>
              {s.text}
              {s.author && (
                <footer className="mt-5 font-mono text-[10px] uppercase not-italic tracking-[0.25em] text-[#C87D55]">
                  {s.author}
                </footer>
              )}
            </blockquote>
          </Reveal>
        )}

        {s.type === "timeline" && (
          <div className={`border-l ${line(theme)} ml-2`}>
            {s.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.06} className="relative pb-10 pl-10 last:pb-0">
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rotate-45 bg-[#C87D55]" />
                <span className="font-mono text-sm tracking-[0.25em] text-[#C87D55]">{item.year}</span>
                <p className={`mt-2 max-w-2xl text-base leading-relaxed ${m}`}>{item.text}</p>
              </Reveal>
            ))}
          </div>
        )}

        {s.type === "cards" && (
          <div className={`grid gap-px border sm:grid-cols-2 lg:grid-cols-3 ${card(theme)}`}>
            {s.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.06} className={`h-full ${theme === "dark" ? "bg-[#102920]" : "bg-white"} p-8`}>
                <h3 className="font-display text-lg font-semibold tracking-tight">{item.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${m}`}>{item.desc}</p>
                {item.tags && (
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span key={tag} className="border border-[#C87D55]/30 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#C87D55]">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        )}

        {s.type === "team" &&
          s.groups.map((g, gi) => (
            <div key={gi} className={gi > 0 ? "mt-14" : ""}>
              <Reveal>
                <h3 className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#C87D55]">{g.title}</h3>
              </Reveal>
              <div className={`grid gap-px border sm:grid-cols-2 lg:grid-cols-3 ${card(theme)}`}>
                {g.members.map((member, i) => (
                  <Reveal key={i} delay={i * 0.05} className={`${theme === "dark" ? "bg-[#102920]" : "bg-white"} p-7`}>
                    <span className="flex h-12 w-12 items-center justify-center border border-[#C87D55]/40 font-mono text-sm text-[#C87D55]">
                      {member.initials}
                    </span>
                    <h4 className="mt-5 font-display text-lg font-semibold tracking-tight">{member.name}</h4>
                    <span className={`mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] ${m}`}>{member.role}</span>
                    {member.bio && <p className={`mt-4 text-sm leading-relaxed ${m}`}>{member.bio}</p>}
                  </Reveal>
                ))}
              </div>
            </div>
          ))}

        {s.type === "faq" && <FaqAccordion items={s.items} theme={theme} />}
      </div>
    </section>
  );
};

const CtaBand = ({ c }) => {
  const { lang } = useLang();
  const band = NAV[lang].ctaBand;
  return (
    <section className="bg-[#C87D55] py-16 sm:py-20" data-testid="page-cta-band">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[#0A1F18] sm:text-4xl">
            {c.ctaTitle || band.title}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#0A1F18]/70">{c.ctaText || band.text}</p>
        </div>
        <Link
          to="/contact"
          data-testid="page-cta-button"
          className="group flex shrink-0 items-center gap-3 bg-[#0A1F18] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors duration-300 hover:bg-[#102920]"
        >
          {band.button}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </section>
  );
};

export const PageRenderer = ({ page }) => {
  const { lang } = useLang();
  const c = page[lang] || page.en || page.fr;
  return (
    <div data-testid={`page-${page.slug}`}>
      <PageHero c={c} slug={page.slug} />
      {c.sections.map((s, i) => (
        <Section key={i} s={s} index={i} />
      ))}
      <CtaBand c={c} />
    </div>
  );
};
